

import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useCallback } from "react";
import { ListBox } from "primereact/listbox";
import styled from "styled-components";

const DATE_FORMAT = "DD-MM-YYYY";

function getPurchaseAccentColor(purchase) {
    if (purchase?.posted) return "#9CA3AF"; // cool gray (posted)

    switch (purchase?.status) {
        case "Warranty":
            return "#F59E0B"; // amber
        case "Import":
            return "#60A5FA"; // blue
        default:
            return "#FBBF24"; // warm yellow
    }
}

const Shell = styled.div`
  width: 250px;
  height: 100vh;
  padding: 0.5rem;
  background: linear-gradient(180deg, #3f3f46 0%, #2f2f35 100%);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(0, 0, 0, 0.35);
`;

const StyledListBox = styled(ListBox)`
  &.p-listbox {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(10, 10, 10, 0.18);
    border-radius: 14px;
    overflow: hidden;
  }

  .p-listbox-header {
    background: rgba(255, 255, 255, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .p-listbox-filter {
    background: #ffffff !important;
    color: #111827 !important;
    border: 1px solid rgba(17, 24, 39, 0.22) !important;
    border-radius: 10px;
  }

  .p-listbox-filter:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.35) !important;
    border-color: rgba(245, 158, 11, 0.65) !important;
  }

  .p-listbox-filter::placeholder {
    color: rgba(17, 24, 39, 0.55) !important;
  }

  .p-listbox-filter-container .p-listbox-filter-icon {
    color: rgba(17, 24, 39, 0.55) !important;
  }

  .p-listbox-list {
    height: calc(100vh - 1rem);
    padding: 0.35rem;
    background: transparent;
  }

  .p-listbox-item {
    padding: 0.35rem !important;
    background: transparent !important;
    border-radius: 12px;
  }

  .p-listbox-item.p-highlight {
    background: rgba(255, 255, 255, 0.06) !important;
  }
`;

const PurchaseCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;

  width: 100%;
  min-height: 56px;

  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );

  border: 1px solid rgba(255, 255, 255, 0.12);
  border-left: 5px solid ${(p) => p.$accent};
  border-radius: 12px;

  padding: 0.55rem 0.6rem;

  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);

  color: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
`;

const Left = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;

  font-size: 0.7rem;
  opacity: 0.85;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Dot = styled.span`
  opacity: 0.6;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
`;

const StatusPill = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  color: #101014;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  background: ${(p) => p.$accent};
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
`;

const Total = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
`;

export function PurchaseScroll(props) {
    const { setInv, setVisible, purchases, purchaseList } = props;

    const handleSelectPurchase = useCallback(
        (invoiceId) => {
            setInv(invoiceId);
            setVisible(false);
        },
        [setInv, setVisible]
    );

    const itemTemplate = useCallback((purchase) => {
        const accent = getPurchaseAccentColor(purchase);
        const dateText = dayjs(purchase?.date).format(DATE_FORMAT);

        return (
            <PurchaseCard $accent={accent}>
                <Left>
                    <Title>
                        Purchase <span style={{ opacity: 0.95 }}>#{purchase?.id}</span>
                    </Title>
                    <Meta>
                        <span>{dateText}</span>
                        <Dot>•</Dot>
                        <span title={purchase?.customer}>{purchase?.customer}</span>
                    </Meta>
                </Left>

                <Right>
                    <StatusPill $accent={accent}>{purchase?.status}</StatusPill>
                    <Total>{purchase?.total}</Total>
                </Right>
            </PurchaseCard>
        );
    }, []);

    return (
        <Shell>
            <StyledListBox
                filter
                filterPlaceholder="Search by invoice id…"
                value={purchases}
                options={purchaseList}
                optionLabel="id"
                optionValue="id"
                itemTemplate={itemTemplate}
                onChange={(e) => handleSelectPurchase(e.value)}
                aria-label="Purchase list"
            />
        </Shell>
    );
}

PurchaseScroll.propTypes = {
    setInv: PropTypes.func.isRequired,
    refresh: PropTypes.bool.isRequired,
    purchases: PropTypes.number.isRequired,
    setVisible: PropTypes.func.isRequired,
    purchaseList: PropTypes.array.isRequired,
};